import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  Mail,
  Plus,
  Trash2,
  CheckCircle2,
  Send,
  Save,
  Check,
  Radio,
  Zap,
} from 'lucide-react';
import { api, getErrorMessage } from '../lib/api';
import { toast } from '../utils/toast';

export interface QuotaAlertPreferences {
  application_id: string;
  enabled: boolean;
  thresholds: number[];
  additional_emails: string[];
  auto_upgrade_enabled: boolean;
  notify_via_webhook: boolean;
  current_usage: number;
  monthly_quota: number;
  consumption_percentage: number;
}

interface QuotaAlertSettingsProps {
  applicationId: string;
  clientId: string;
  applicationName: string;
}

const AVAILABLE_THRESHOLDS = [
  { value: 75, label: '75%', desc: 'Aviso Prévio (Prevenção inicial)' },
  { value: 85, label: '85%', desc: 'Atenção Operacional (Recomendado)' },
  { value: 95, label: '95%', desc: 'Crítico (Risco iminente de HTTP 429)' },
  { value: 100, label: '100%', desc: 'Quota Esgotada' },
];

export const QuotaAlertSettings: React.FC<QuotaAlertSettingsProps> = ({
  applicationId,
  clientId,
  applicationName,
}) => {
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(true);
  const [selectedThresholds, setSelectedThresholds] = useState<number[]>([75, 85, 95, 100]);
  const [emails, setEmails] = useState<string[]>([]);
  const [newEmailInput, setNewEmailInput] = useState('');
  const [autoUpgrade, setAutoUpgrade] = useState(false);
  const [notifyWebhook, setNotifyWebhook] = useState(true);

  // 1. Fetch current settings
  const { data: alertData, isLoading } = useQuery<{ success: boolean; data: QuotaAlertPreferences }>({
    queryKey: ['quota-alerts', clientId, applicationId],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: QuotaAlertPreferences }>(
        `/clients/${clientId}/applications/${applicationId}/quota-alerts`
      );
      return response.data;
    },
    enabled: !!clientId && !!applicationId,
  });

  useEffect(() => {
    if (alertData?.data) {
      setEnabled(alertData.data.enabled !== false);
      setSelectedThresholds(alertData.data.thresholds || [75, 85, 95, 100]);
      setEmails(alertData.data.additional_emails || []);
      setAutoUpgrade(!!alertData.data.auto_upgrade_enabled);
      setNotifyWebhook(alertData.data.notify_via_webhook !== false);
    }
  }, [alertData]);

  // 2. Save Mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        enabled,
        thresholds: selectedThresholds.sort((a, b) => a - b),
        additional_emails: emails,
        auto_upgrade_enabled: autoUpgrade,
        notify_via_webhook: notifyWebhook,
      };
      const response = await api.put(
        `/clients/${clientId}/applications/${applicationId}/quota-alerts`,
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success('Preferências de alerta de quota guardadas com sucesso!');
      void queryClient.invalidateQueries({ queryKey: ['quota-alerts', clientId, applicationId] });
    },
    onError: (err) => {
      toast.error(`Falha ao guardar alertas: ${getErrorMessage(err)}`);
    },
  });

  // 3. Test Alert Mutation
  const testAlertMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(
        `/clients/${clientId}/applications/${applicationId}/quota-alerts/test`
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success('Alerta de teste enviado com sucesso para todos os canais configurados!');
    },
    onError: (err) => {
      toast.error(`Falha no envio de teste: ${getErrorMessage(err)}`);
    },
  });

  const toggleThreshold = (val: number) => {
    setSelectedThresholds((prev) =>
      prev.includes(val) ? prev.filter((t) => t !== val) : [...prev, val]
    );
  };

  const handleAddEmail = (e: React.FormEvent) => {
    e.preventDefault();
    const clean = newEmailInput.trim().toLowerCase();
    if (!clean) return;

    // Simple email regex validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(clean)) {
      toast.warning('Introduza um endereço de e-mail válido.');
      return;
    }

    if (emails.includes(clean)) {
      toast.warning('Este endereço de e-mail já está adicionado.');
      return;
    }

    setEmails((prev) => [...prev, clean]);
    setNewEmailInput('');
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setEmails((prev) => prev.filter((e) => e !== emailToRemove));
  };

  const currentUsage = alertData?.data?.current_usage || 0;
  const monthlyQuota = alertData?.data?.monthly_quota || 10000;
  const consumptionPercentage =
    alertData?.data?.consumption_percentage ||
    Number(((currentUsage / monthlyQuota) * 100).toFixed(1));

  if (isLoading) {
    return (
      <div className="p-6 text-center text-xs text-slate-500 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
        A carregar configurações de alerta...
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-7 border border-slate-200 dark:border-slate-800 shadow-sm space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-slate-100 dark:border-slate-800">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 flex items-center justify-center">
              <Bell className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900 dark:text-white">
                Alertas de Quota & Notificações Proativas
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Defina quando e para quem enviar notificações antes que o tráfego atinja <code>HTTP 429</code>.
              </p>
            </div>
          </div>
        </div>

        {/* Master ON/OFF Toggle */}
        <label className="flex items-center gap-2.5 cursor-pointer self-end sm:self-auto select-none">
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">
            {enabled ? 'Alertas Ativos' : 'Alertas Desativados'}
          </span>
          <div
            onClick={() => setEnabled((prev) => !prev)}
            className={`w-11 h-6 rounded-full transition-colors relative cursor-pointer p-0.5 ${
              enabled ? 'bg-primary-600' : 'bg-slate-300 dark:bg-slate-700'
            }`}
          >
            <div
              className={`w-5 h-5 rounded-full bg-white transition-transform transform shadow-sm ${
                enabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </div>
        </label>
      </div>

      {/* Real-time Usage Gauge */}
      <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200/80 dark:border-slate-800 space-y-2">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between text-xs gap-1">
          <span className="font-semibold text-slate-700 dark:text-slate-300">
            Consumo Mensal Atual ({applicationName})
          </span>
          <div className="font-mono text-slate-500">
            <strong>{currentUsage.toLocaleString('pt-PT')}</strong> / {monthlyQuota.toLocaleString('pt-PT')} reqs (
            <span
              className={
                consumptionPercentage >= 95
                  ? 'text-rose-600 font-bold'
                  : consumptionPercentage >= 80
                  ? 'text-amber-600 font-bold'
                  : 'text-emerald-600 font-bold'
              }
            >
              {consumptionPercentage}%
            </span>
            )
          </div>
        </div>

        <div className="w-full h-2.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${
              consumptionPercentage >= 95
                ? 'bg-rose-500'
                : consumptionPercentage >= 80
                ? 'bg-amber-500'
                : 'bg-primary-500'
            }`}
            style={{ width: `${Math.min(100, consumptionPercentage)}%` }}
          />
        </div>
      </div>

      {/* Thresholds Selection Checklist */}
      <div className="space-y-3">
        <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 block">
          Limiares de Disparo de Alerta
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
          {AVAILABLE_THRESHOLDS.map((t) => {
            const isSelected = selectedThresholds.includes(t.value);
            return (
              <div
                key={t.value}
                onClick={() => enabled && toggleThreshold(t.value)}
                className={`p-3 rounded-xl border text-xs cursor-pointer transition-all select-none ${
                  !enabled
                    ? 'opacity-50 cursor-not-allowed border-slate-200 dark:border-slate-800'
                    : isSelected
                    ? 'border-primary-500 bg-primary-50/60 dark:bg-primary-950/40 text-primary-900 dark:text-primary-200 ring-1 ring-primary-500/20'
                    : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-sm">{t.label}</span>
                  {isSelected ? (
                    <CheckCircle2 className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  ) : (
                    <div className="w-4 h-4 rounded-full border border-slate-300 dark:border-slate-600" />
                  )}
                </div>
                <span className="text-[11px] text-slate-500 dark:text-slate-400 block leading-snug">
                  {t.desc}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Additional Recipients Section */}
      <div className="space-y-3 pt-2 border-t border-slate-100 dark:border-slate-800">
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 block">
            Destinatários Adicionais de Notificação
          </label>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Adicione e-mails da sua equipa técnica ou financeira (ex.: DevOps de plantão, CTO) que devem receber alertas em simultâneo com a conta principal.
          </p>
        </div>

        {/* Email Input Form */}
        <form onSubmit={handleAddEmail} className="flex gap-2">
          <div className="relative flex-1">
            <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="email"
              disabled={!enabled}
              placeholder="exemplo: devops@empresa.ao"
              value={newEmailInput}
              onChange={(e) => setNewEmailInput(e.target.value)}
              className="input-modern text-xs py-2 pl-9 w-full"
            />
          </div>
          <button
            type="submit"
            disabled={!enabled || !newEmailInput.trim()}
            className="btn-base btn-secondary btn-sm text-xs px-3 py-2 inline-flex items-center gap-1"
          >
            <Plus className="w-3.5 h-3.5" /> Adicionar
          </button>
        </form>

        {/* Emails List Tags */}
        {emails.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {emails.map((email) => (
              <span
                key={email}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 shadow-2xs"
              >
                <span>{email}</span>
                {enabled && (
                  <button
                    type="button"
                    onClick={() => handleRemoveEmail(email)}
                    className="text-slate-400 hover:text-rose-600 transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Advanced Notification Channels & Continuous Protection */}
      <div className="space-y-3 pt-3 border-t border-slate-100 dark:border-slate-800">
        <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 block">
          Canais & Continuidade de Negócio
        </label>

        <div className="space-y-2.5">
          <label className="flex items-start gap-2.5 cursor-pointer text-xs select-none">
            <input
              type="checkbox"
              disabled={!enabled}
              checked={notifyWebhook}
              onChange={(e) => setNotifyWebhook(e.target.checked)}
              className="mt-0.5 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
            />
            <div>
              <span className="font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                <Radio className="w-3.5 h-3.5 text-indigo-500" />
                Despachar Webhook de Alerta (<code>quota.warning</code>)
              </span>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                Envia payload JSON assinado via HMAC para os endpoints de webhook registados nesta aplicação.
              </p>
            </div>
          </label>

          <label className="flex items-start gap-2.5 cursor-pointer text-xs select-none">
            <input
              type="checkbox"
              disabled={!enabled}
              checked={autoUpgrade}
              onChange={(e) => setAutoUpgrade(e.target.checked)}
              className="mt-0.5 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
            />
            <div>
              <span className="font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5 text-amber-500" />
                Proteção contra Interrupção (Débito de Excedente em Carteira)
              </span>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                Em caso de esgotamento aos 100%, autoriza a cobrança de transações excedentes debitando o saldo disponível na carteira sem bloquear requisições.
              </p>
            </div>
          </label>
        </div>
      </div>

      {/* Action Footer */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
        <button
          type="button"
          onClick={() => testAlertMutation.mutate()}
          disabled={!enabled || testAlertMutation.isPending}
          className="btn-base btn-secondary btn-sm text-xs px-4 py-2 inline-flex items-center gap-1.5 w-full sm:w-auto"
        >
          <Send className={`w-3.5 h-3.5 ${testAlertMutation.isPending ? 'animate-spin' : ''}`} />
          <span>{testAlertMutation.isPending ? 'A enviar teste...' : 'Enviar Alerta de Teste'}</span>
        </button>

        <button
          type="button"
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          className="btn-base btn-primary btn-sm text-xs px-5 py-2 inline-flex items-center gap-1.5 w-full sm:w-auto shadow-xs"
        >
          {saveMutation.isPending ? (
            <Save className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Check className="w-3.5 h-3.5" />
          )}
          <span>{saveMutation.isPending ? 'A guardar...' : 'Guardar Preferências'}</span>
        </button>
      </div>
    </div>
  );
};
